<template>
  <Dropfile
    :disabled="readonly || isUploading"
    @input="onFileInput"
    @dragging="dragging = $event"
    :data-folder-id="folder.id"
    class="px-2 xl:px-4"
    :aria-label="getFolderName(folder)"
    :id="folder.id + ''"
  >
    <div
      class="relative"
      :class="{
        'rounded-md border-2 border-grey-925': !isRoot,
        'not-draggable not-draggable-over': isRoot,
        'pb-2': !(folder.documents || []).length && !dragging,
        'py-4': isRoot && dragging,
      }"
    >
      <div v-if="!isRoot" class="flex items-center bg-grey-925 pl-3 pr-1 h-6" id="drop-hover">
        <PTooltip :label="folder.name" class="overflow-hidden" :delay="500" :offset="5">
          <NameInput
            class="font-bold"
            :initial-value="folder.name"
            :readonly="readonly || !canEdit"
            ref="nameInput"
            @submit="updateFolderName(folder, $event)"
            type="classeur"
            :id="folder.id"
            data-sentry-id="FolderNameInput"
          />
        </PTooltip>
        <PButton
          v-if="!readonly && folder.position > 1"
          @click="emit('move', 'up')"
          size="icon"
          mode="tertiary-borderless"
          :aria-label="`déplacer vers le haut ${folder.name}`"
          class="sr-only focus:not-sr-only"
          :data-sentry-id="`FolderMoveUpBtn-${folder.id}`"
        >
          <OhVueIcon :name="upIcon" scale="2" />
        </PButton>
        <PButton
          v-if="!readonly && !isLast"
          @click="emit('move', 'down')"
          size="icon"
          mode="tertiary-borderless"
          :aria-label="`déplacer vers le bas ${folder.name}`"
          class="sr-only focus:not-sr-only"
          :data-sentry-id="`FolderMoveDownBtn-${folder.id}`"
        >
          <OhVueIcon :name="downIcon" scale="2" />
        </PButton>
        <div class="ml-auto flex items-center">
          <OhVueIcon
            v-if="!readonly && canEdit"
            :name="dragIcon"
            scale="1.2"
            class="ml-auto mr-1v text-blue-france-sun-113 drag-handle cursor-move"
          />
          <DocLinkForm v-if="!readonly" :root-folder-id="folder.id" v-model="isDocLinkFormOpened" />
          <FileLinkLoader :id="folder.id" type="folder" :disabled="isDisabled" v-model="isFileLinkOpened1" />
          <FileLinkLoader :id="folder.id" type="folder" :disabled="isDisabled" mailto v-model="isFileLinkOpened2" />
          <KebabMenu
            size="sm"
            class="text-sm text-grey-50"
            :class="{ 'ml-auto': readonly }"
            placement="left-start"
            :aria-label="`Action rapide sur le dossier ${folder.name}`"
            :aria-describedby="folder.id + ''"
          >
            <template #default="{ close }">
              <button
                v-if="!readonly"
                @click="addDocument(close)"
                :disabled="isUploading"
                class="text-start disabled:text-grey-625 disabled:cursor-not-allowed"
                data-sentry-id="FolderAddDocBtn"
              >
                Ajouter Document(s)
              </button>
              <button @click="isDocLinkFormOpened = true" class="text-start">Ajouter un lien</button>
              <button @click="isFileLinkOpened1 = true" class="text-start" data-sentry-id="FileLinkLoaderDlBtn">
                Télécharger
              </button>
              <button @click="isFileLinkOpened2 = true" class="text-start" data-sentry-id="FileLinkLoaderDlBtn">
                Envoyer par mail
              </button>
              <PTooltip label="Action impossible sur un dossier archivé" :disabled="canEdit" :delay="500" :offset="5">
                <button
                  v-if="!readonly"
                  :disabled="!canEdit"
                  :class="{
                    'text-grey-425 cursor-not-allowed': !canEdit,
                    'text-error-425': canEdit,
                  }"
                  @click="removeFolder(folder.id, close)"
                  class="border-t pt-1v border-grey-925 text-start w-full"
                  :data-sentry-id="`FolderRemoveBtn-${folder.id}`"
                >
                  Supprimer
                </button>
              </PTooltip>
            </template>
          </KebabMenu>
        </div>
      </div>
      <div
        v-if="dragging"
        class="absolute z-50 top-0 left-0 right-0 bottom-0 border-dotted rounded-md border-grey-125 border-2 text-black font-medium"
        :class="{
          'mt-6': !isRoot,
        }"
      >
        <div class="flex bg-transparent backdrop-blur-md flex-col items-center justify-center h-full">
          <span>Glissez et déposez vos documents</span>
          <p class="line-clamp-1">
            <span class="text-sm">Destination : </span>
            <span class="italic text-sm">{{ getFolderName(folder) }}</span>
          </p>
          <span class="text-sm">
            Taille maximum par document:
            {{ config.fileSizeLimit.replace("i", "").replaceAll("B", "o") }}
          </span>
        </div>
      </div>
      <DraggableList
        class="flex flex-col gap-2"
        :class="{
          'px-2': (!isRoot && folder.documents?.length) || dragging,
          'py-2': dragging || !isRoot,
          'py-6': dragging && !folder.documents?.length,
        }"
        group-name="documents"
        data-id-attr="data-document-id"
        @drop="reorderDocs"
        handle="svg.drag-handle"
        @choose="draggedDocIndex = $event.oldIndex!"
        @un-choose="draggedDocIndex = null"
      >
        <Document
          v-for="(doc, i) of folder.documents || []"
          :key="doc.id"
          :doc="doc"
          :readonly="readonly"
          :is-first="i === 0"
          :is-last="i >= documents.length - 1"
          :is-first-folder="isFirst"
          :is-last-folder="isLast"
          @move="moveDoc(doc, $event)"
          @edit="emit('editDoc', $event)"
          :class="{
            'bg-blue-cumulus-975!': draggedDocIndex === i,
          }"
        />
      </DraggableList>
    </div>
  </Dropfile>
</template>

<script setup lang="ts">
import { FormOperation, type ParapheurConfig } from "@/app/types";
import { AlertDuration, AlertType, PButton, PTooltip, useAlertManager, useConfig, useIcons } from "@pasta/box-v2";
import { clone, findIndex, flatten, xor } from "lodash";
import { OhVueIcon } from "oh-vue-icons";
import { MdDraghandle, RiArrowDropDownLine, RiArrowDropUpLine } from "oh-vue-icons/icons";
import { type SortableEvent } from "sortablejs";
import { computed, inject, ref } from "vue";
import DraggableList from "../../../components/DraggableList.vue";
import FileLinkLoader from "../../../components/FileLinkLoader.vue";
import KebabMenu from "../../../components/KebabMenu.vue";
import NameInput from "../../../components/NameInput.vue";
import { useNotifyAsyncOperation } from "../../../composable/useAsyncOperation";
import { ROOT_FOLDER_NAME } from "../../../consts";
import { type LetterFileDocumentDto } from "../../../dto/letter-file-document.dto";
import { type LetterFileFolderDto } from "../../../dto/letter-file-folder.dto";
import { type LetterFileDto } from "../../../dto/letter-file.dto";
import { useLetterFileDocumentApi } from "../../../http/letter-file-document.api";
import { useLetterFileFolderApi } from "../../../http/letter-file-folder.api";
import { useLetterFileStore } from "../../../stores/letter-file.store";
import { canEditAfterArchival, type FormProvide, FormToken } from "../utils";
import DocLinkForm from "./DocLinkForm.vue";
import Document from "./Document.vue";
import Dropfile from "./Dropfile.vue";
import { findDocumentsByIds, getFolderName } from "./utils";

interface Props {
  folder: LetterFileFolderDto;
  isFirst: boolean;
  isLast: boolean;
  readonly: boolean;
  isUploading: boolean;
}
const props = defineProps<Props>();
const config = useConfig<ParapheurConfig>();
const letterFileStore = useLetterFileStore();

const folderApi = useLetterFileFolderApi();
const documentApi = useLetterFileDocumentApi();
const notifyAsyncOperation = useNotifyAsyncOperation();
const { refresh, letterFile } = inject<FormProvide>(FormToken)!;

const canEdit = computed(() => canEditAfterArchival(letterFile.value!, props.folder));

const [dragIcon, upIcon, downIcon] = useIcons(MdDraghandle, RiArrowDropUpLine, RiArrowDropDownLine);

const emit = defineEmits<{
  (e: "addDocument"): void;
  (e: "uploadFiles", files: File[]): void;
  (e: "move", direction: "up" | "down"): void;
  (e: "moveDoc", args: { doc: LetterFileDocumentDto; direction: "up" | "down" }): void;
  (e: "editDoc", id: number): void;
}>();

const isRoot = computed(() => props.folder.name === ROOT_FOLDER_NAME);
const documents = computed(() => props.folder.documents || []);

const nameInput = ref<any>(null);
const isDocLinkFormOpened = ref(false);
const isFileLinkOpened1 = ref(false);
const isFileLinkOpened2 = ref(false);

async function updateFolderName(folder: LetterFileFolderDto, name: string) {
  if (!name) return;

  const existingFolder = letterFile.value?.folders
    ?.filter(folder => folder.id !== props.folder.id)
    .find(folder => folder.name.toLowerCase() === name.toLowerCase());
  if (existingFolder) {
    if (!confirm("Un autre dossier porte déjà ce nom, confirmez pour fusionner les deux dossiers.")) {
      return;
    }
  }

  const newName = existingFolder ? existingFolder.name : name;
  if (folder.name !== newName) {
    folder.name = newName; // optimist
    try {
      await notifyAsyncOperation(
        async () =>
          folderApi.update(folder.id, {
            name: newName,
            letterFile: letterFile.value!.id,
          }),
        `${FormOperation.FOLDER_NAME}-${props.folder.id}`
      );
    } catch {
      useAlertManager().createAlert({
        message: "Le classeur n'a pas pu être renommé",
        duration: AlertDuration.MEDIUM,
        type: AlertType.ERROR,
      });
    } finally {
      await notifyAsyncOperation(refresh, FormOperation.REFRESH);
    }
  }
}

async function removeFolder(id: number, closeMenu: () => void) {
  if (confirm("Êtes vous sûr(e) de vouloir supprimer ce classeur et tous ses documents ?")) {
    await notifyAsyncOperation(() => folderApi.delete(id, letterFile.value!.id), FormOperation.FOLDER);
    await notifyAsyncOperation(refresh, FormOperation.REFRESH);
  }
  closeMenu();
}

function addDocument(close: () => void) {
  emit("addDocument");
  close();
}

const draggedDocIndex = ref<number | null>(null);

async function reorderDocs(_event: SortableEvent, docIds: number[]) {
  try {
    if (documents.value.length !== docIds.length) {
      const movedDocId = xor([...documents.value.map(d => d.id)].sort(), [...docIds].sort()).pop();

      if (movedDocId) {
        updateDocumentFolderInStore(letterFile.value!, movedDocId, docIds);
        await notifyAsyncOperation(
          async () =>
            documentApi.update(movedDocId, letterFile.value!.id, {
              folder: props.folder.id,
            }),
          FormOperation.DOCUMENT
        );
      }
    }

    updateDocsOrderInStore(letterFile.value!, docIds);
    await notifyAsyncOperation(
      async () => folderApi.reorder(props.folder.id, letterFile.value!.id, docIds),
      FormOperation.FOLDER
    );
  } catch {
    useAlertManager().createAlert({
      message: "Le document n'a pas pu être déplacé",
      type: AlertType.ERROR,
      duration: AlertDuration.MEDIUM,
    });
  } finally {
    await notifyAsyncOperation(refresh, FormOperation.REFRESH);
  }
}

async function moveDoc(doc: LetterFileDocumentDto, direction: "up" | "down") {
  const isFirst = doc.position === 0;
  const clonedDocs = clone(documents.value);
  const docIndex = findIndex(clonedDocs, doc);
  const isLast = docIndex >= clonedDocs.length - 1;

  if ((isFirst && direction === "up") || (isLast && direction === "down")) {
    emit("moveDoc", { doc, direction });
  } else {
    const fromIndex = docIndex;
    const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
    const el = clonedDocs.splice(fromIndex, 1)[0];
    clonedDocs.splice(toIndex, 0, el);
    await notifyAsyncOperation(async () => {
      await folderApi.reorder(
        props.folder.id,
        letterFile.value!.id,
        clonedDocs.map(doc => doc.id)
      );
      await refresh();
    }, FormOperation.FOLDER);
  }
}

function updateDocumentFolderInStore(lf: LetterFileDto, movedDocId: number, docIds: number[]) {
  const movedDoc = flatten(lf.folders!.map(folder => folder.documents || [])).find(doc => doc.id === movedDocId);
  if (!movedDoc) return;
  const targetDocs = lf.folders!.find(folder => folder.id === props.folder.id)?.documents || [];
  const orderedDocs = findDocumentsByIds([...targetDocs, movedDoc], docIds);
  // Skipping is safe: the `refresh()` of `reorderDocs` resyncs the store with the server
  if (orderedDocs.length !== docIds.length) return;

  lf.folders = lf.folders!.map(folder => {
    folder.documents =
      folder.id === props.folder.id ? orderedDocs : (folder.documents || []).filter(doc => doc.id !== movedDocId);
    return folder;
  });
  letterFileStore.setOne(lf);
}

function updateDocsOrderInStore(lf: LetterFileDto, docIds: number[]) {
  const targetDocs = lf.folders!.find(folder => folder.id === props.folder.id)?.documents || [];
  const orderedDocs = findDocumentsByIds(targetDocs, docIds);
  // Skipping is safe: the `refresh()` of `reorderDocs` resyncs the store with the server
  if (orderedDocs.length !== docIds.length) return;

  lf.folders = lf.folders!.map(folder => {
    if (folder.id === props.folder.id) folder.documents = orderedDocs;
    return folder;
  });
  letterFileStore.setOne(lf);
}

// Uploading new files through drag&drop
const dragging = ref<boolean>(false);
async function onFileInput(files: File[]) {
  if (props.readonly) return;
  emit("uploadFiles", files);
}

const isDisabled = computed(() => {
  return documents.value.length <= 0;
});
</script>
